import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaData } from '../hooks/useMediaData';
import { useAuth } from './AuthContext';

const MediaDataContext = createContext();

export const useMediaDataContext = () => {
  const context = useContext(MediaDataContext);
  if (!context) {
    throw new Error('useMediaDataContext must be used within a MediaDataProvider');
  }
  return context;
};

export const MediaDataProvider = React.memo(({ children, supabase }) => {
  const { currentUser, isAdmin, loading } = useAuth();
  const location = useLocation();

  // Determine if we should skip auto-loading collection based on current route
  // Only load collection automatically for pages that actually need it
  const needsCollection = useMemo(() => {
    const path = location.pathname;
    const needs =
      path.startsWith('/collection') || // Collection pages need full collection
      path.startsWith('/trade') || // Trade pages need collection data
      path.startsWith('/community') || // Community trading tab needs collection for offer search
      path.includes('/marketplace') || // Marketplace might need collection for owned items
      path.startsWith('/admin'); // Admin pages might need collection data

    // Debug logging (reduced for duplicate prevention)
    if (process.env.NODE_ENV === 'development' && needs) {
      console.log('🔍 MediaDataContext - Loading collection for:', path);
    }

    return needs;
  }, [location.pathname]);

  // Use the existing useMediaData hook to get all the functionality
  const vhsData = useMediaData(supabase, currentUser, isAdmin, loading, {
    skipAutoCollection: !needsCollection,
  });

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => vhsData, [vhsData]);

  return <MediaDataContext.Provider value={contextValue}>{children}</MediaDataContext.Provider>;
});
